import React, { Fragment, useEffect, useRef, useState, useMemo } from "react";
import * as StateLib from "@/Utils/StateLib";
import {
  Badge,
  Button,
  Checkbox,
  Divider,
  Flex,
  isNullish,
  Search,
  Text
} from "@theorgchartdev/storybook";
import {
  filterResults,
  GetModifiedFields,
  getShownCountFeedbackText,
  IsEditableField,
  sortByKey,
  GetMergeAppendFieldSources,
  FormatDataSource
} from "@/appComponents/Blocks/Cards/RecordInfoCard/utils";
import cx from "classnames";
import { GetDoc, GetWSMgr, ReRenderCanvas } from "@/Utils/StateLib";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { FieldsTable } from "@/appComponents/Blocks/Cards/RecordInfoCard/FieldsTable";
import { Globals } from "@coreapp/CoreApp/Globals";
import { appstr } from "@coreapp/utils/appstr";
import { appstrp } from "@coreapp/utils/appstrp";
import { internalOnlyFields, kPositionCountFields, kEmployeeCountFields } from "@coreapp/constants";

export const RecordDetailsFields = props => {
  const doc = GetDoc();
  const iWiz = doc.GetIWizData();

  const [showSourceColumn, setShowSourceColumn] = useState(false);
  const [initialEditField, setInitialEditField] = useState(null);
  const [editingFields, setEditingFields] = useState([]);
  const [editingAllFields, setEditingAllFields] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const { canEditDocuments } = usePermissionCheck();

  const canAccessEditing =
    canEditDocuments && !doc?.IsServerChart() && Globals._uiConfig?.InEditMode();

  const isEditing = props.editing === true;

  const sourceData = props.dbObj.GetFieldDefByID("_objInfo");

  const fieldSources = useMemo(() => {
    var fieldSourceMaps = iWiz.GetFieldSourceMaps();
    if (!fieldSourceMaps) return { merge: [], append: [] };
    return props.dbObj?.IsPositionObj() ? fieldSourceMaps.positions : fieldSourceMaps.holders;
  }, [props.dbObj, iWiz]);

  useEffect(() => {
    if (!isEditing) {
      setInitialEditField(null);
      setEditingFields([]);
      setEditingAllFields(false);
    }
  }, [isEditing]);

  const importConfig = useMemo(() => {
    const fImport = iWiz?.GetImport();
    const positionConfig = iWiz?.fPositionConfig;
    return positionConfig?.isPositionChart()
      ? positionConfig.isPositionSynth()
        ? fImport
        : props.dbObj?.IsPositionObj()
          ? fImport.positions
          : fImport.holders
      : fImport;
  }, [iWiz, props.dbObj]);

  const { fields, sourceMap } = useMemo(() => {
    const { sourceFieldsObj, fieldSourceMap, mergeAppendFieldNames } = GetMergeAppendFieldSources(
      props.dbObj,
      fieldSources
    );
    var fieldDefList = props.dbObj?.fParentClass.fFieldList;
    const isPositionObj = props.dbObj?.IsPositionObj?.() === true;

    // Fields to filter based on object type

    const fields = fieldDefList.reduce((acc, field) => {
      const fieldName = field.GetFieldName();

      // Filter out employee count fields from positions and position count fields from employees
      if (isPositionObj === true && kEmployeeCountFields.includes(fieldName)) {
        return acc;
      }
      if (isPositionObj === false && kPositionCountFields.includes(fieldName)) {
        return acc;
      }

      if (field.IsReserved() && field.GetCalcCode() >= -1) {
        !acc["--"] ? (acc["--"] = [field]) : acc["--"].push(field);
      } else if (!mergeAppendFieldNames.has(fieldName)) {
        if (!acc["Main"]) {
          acc["Main"] = [field];
        } else {
          acc["Main"].push(field);
        }
      }

      return acc;
    }, sourceFieldsObj);
    return { fields, sourceMap: fieldSourceMap };
  }, [props.dbObj, fieldSources]);

  const sourceMapWithMain = useMemo(() => {
    const mapWithMain = new Map(sourceMap);
    if (!importConfig) return mapWithMain;
    mapWithMain.set("Main", FormatDataSource(importConfig, "main"));
    return mapWithMain;
  }, [sourceMap, importConfig]);

  const onInput = input => {
    setSearchTerm(input);
  };

  const handleInput = string => {
    onInput(string);
  };
  const handleResetInput = () => {
    onInput("");
  };

  const handleReset = (unsavedCheck = true) => {
    const proceed = props.onReset(unsavedCheck);

    if (proceed) {
      setInitialEditField(null);
      setEditingFields([]);
      setEditingAllFields(false);
    }
  };

  const handleSave = () => {
    props.onSave();
    setInitialEditField(null);
    setEditingFields([]);
    setEditingAllFields(false);
  };

  const handleStartEditOneField = (e, fVPair) => {
    if (!canAccessEditing) return;
    if ((!isNullish(fVPair?.fieldDef) && !IsEditableField(fVPair.fieldDef)) || editingAllFields)
      return;
    setInitialEditField(fVPair?.fieldName);
    setEditingFields([...editingFields, fVPair?.fieldName]);
    props.onEdit(null, true);
  };

  const handleStartEditAllFields = e => {
    if (!canAccessEditing) return;
    if (isEditing && !editingAllFields) {
      setEditingAllFields(true);
    } else {
      props.onEdit();
      setEditingAllFields(editingAllFields => !editingAllFields);
    }
  };

  let unfilteredData = [...props.data].filter(fVPair => {
    if (fVPair.fieldDef) {
      const isInternal = internalOnlyFields.includes(fVPair.fieldDef.GetFieldID());
      if (!isInternal) return true;

      // Add non-serializable internal fields (e.g. _Headcount) to the record details, but only in Custom Charts.
      return doc.IsCustomChart() && !fVPair.fieldDef.IsSerializable();
    }
    return true;
  });

  const getFilteredData = () => {
    let filteredData = unfilteredData;
    const fieldDefs = unfilteredData.map(item => item.fieldDef);
    if (!isNullish(searchTerm) && searchTerm.length > 0)
      filteredData = unfilteredData.filter(fVPair => filterResults(fVPair, searchTerm));
    filteredData = filteredData.map(item => {
      return { ...item, source: getSourceForField(item.fieldDef, fieldDefs) };
    });
    return filteredData;
  };

  const getSourceForField = (field, fieldDefs) => {
    const sources = Object.keys(fields);
    for (let source of sources) {
      const dataSourceFieldDefList = fields[source].filter(function (x) {
        const isInternal = internalOnlyFields.includes(x.GetFieldID());
        if (fieldDefs.includes(x) && (!isInternal || (doc.IsCustomChart() && !x.IsSerializable())))
          return x;
      });
      if (dataSourceFieldDefList.includes(field)) return source;
    }
    return sources[0];
  };

  const sortedData = getFilteredData();

  const showFilterFeedbackText = unfilteredData.length > 0 && searchTerm.length > 0;

  const feedbackText = showFilterFeedbackText
    ? getShownCountFeedbackText(unfilteredData, sortedData)
    : undefined;

  const editButtonClass = cx("oc-special-secondary", "oc-special-secondary--fit");

  return (
    <Flex direction={"column"} width={"100%"} gap={"12px"} flex={"1"} overflow={"hidden"}>
      <Flex direction={"column"} width={"100%"} gap={"12px"}>
        <Flex gap={"4px"} padding={"0 0 0 2px"} direction={"column"}>
          <Flex gap={"12px"} alignItems={"center"} justifyContent={"space-between"}>
            <Flex width={"50%"} alignItems={"center"} padding={"2px"} gap={"12px"}>
              <Search
                dataTestId={"record-details-search"}
                onInput={handleInput}
                onResetInput={handleResetInput}
                placeholder={appstrp("Search record", "...")}
                timeout={0}
                size="small"
              />
            </Flex>
            <Flex
              flex={"1"}
              justifyContent={"flex-end"}
              gap={"24px"}
              alignItems={"center"}
              padding={"2px"}
            >
              {sourceData && (
                <Checkbox
                  id={"cb-show-source"}
                  label={appstr("Show source")}
                  size={"small"}
                  checked={showSourceColumn}
                  onChange={() => setShowSourceColumn(!showSourceColumn)}
                />
              )}
              {canAccessEditing && (
                <Button
                  dataTestId={"record-details-edit-btn"}
                  modifier={"secondary"}
                  className={editingAllFields ? editButtonClass : undefined}
                  iconOnly
                  size={"small"}
                  iconProps={{ name: "pen" }}
                  onClick={editingAllFields ? handleSave : handleStartEditAllFields}
                />
              )}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      <Flex direction={"column"} overflow={"hidden"} height={"100%"}>
        <Divider />
        <FieldsTable
          onSave={handleSave}
          className={"oc-record-info"}
          data={sortedData}
          delta={props.deltaRef}
          canAccessEdit={canAccessEditing}
          isEditing={editingAllFields}
          initialEditField={initialEditField}
          editingFields={editingFields}
          showSourceField={showSourceColumn}
          handleStartEdit={handleStartEditOneField}
          onUpdateField={props.onUpdate}
          handleReset={handleReset}
          setInitialEdit={setInitialEditField}
          sourceMap={sourceMapWithMain}
        />
      </Flex>
      <Flex justifyContent="space-between" width={"100%"}>
        <Flex alignItems={"center"}>
          <Text colorModifier={"lightgrey"} size={"xsmall"} style={{ paddingLeft: "4px" }}>
            {feedbackText ?? ""}
          </Text>
        </Flex>
        <Flex gap="12px" justifyContent={"flex-end"}>
          {isEditing && (
            <Fragment>
              <div key={appstr("Cancel")} style={{ width: "88px" }}>
                <Button
                  isFullWidth
                  size="small"
                  modifier={"tertiary"}
                  onClick={handleReset}
                  dataTestId={`record-details-field-edit-${appstr("Cancel")}`}
                >
                  {appstr("Cancel")}
                </Button>
              </div>
              <div key={appstr("Save")} style={{ width: "88px" }}>
                <Button
                  isFullWidth
                  size="small"
                  modifier={"primary"}
                  onClick={handleSave}
                  dataTestId={`record-details-field-edit-${appstr("Save")}`}
                >
                  {appstr("Save")}
                </Button>
              </div>
            </Fragment>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};
